import { ChevronNav } from "@theme/assets";
import { AnimatePresence, m } from "framer-motion";
import { type FC, type ReactNode, useCallback, useEffect, useRef, useState } from "react";

import { Box, Button, getThemeValue } from "@theme/components";

import { onKeyDown, useClickOutside, useIsTouchDevice, useOnNavigate } from "@theme/utils";

import type { MsComponentPropsWithoutRef } from "@theme/types/global";

export interface NavLangDropdownProps extends MsComponentPropsWithoutRef<"div"> {
  alignment: "left" | "center" | "right";
  buttonLabel: ReactNode | string;
  openAction?: "click" | "hover";
}

const shouldCloseOnClick = (target: EventTarget | null) => {
  const el = target as HTMLElement | null;
  if (!el) return false;

  if (el.closest("[data-keep-open]")) return false;

  return !!el.closest('[role="menuitem"], a[href], button, [data-close-on-click]');
};

export const DropdownMenu: FC<NavLangDropdownProps> = ({
  buttonLabel,
  children,
  "aria-label": ariaLabel,
  alignment,
  sx,
  className,
  openAction = "click",
  ...props
}) => {
  const [showDropShadow, setShowDropShadow] = useState(false);
  const [isOpen, setOpen] = useState(false);

  const dropdownButtonRef = useRef<HTMLButtonElement | null>(null);
  const dropdownMenuRef = useRef<HTMLDivElement | null>(null);
  const hoverTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const isTouchDevice = useIsTouchDevice();

  const effectiveOpenAction = isTouchDevice ? "click" : openAction;

  // Open/close handlers call parent setter and optional callbacks.
  const handleOpen = useCallback(() => setOpen(true), []);
  const handleClose = useCallback(() => setOpen(false), []);

  useOnNavigate(() => isOpen && handleClose());
  useClickOutside(dropdownMenuRef, () => {
    setOpen(false);
  });

  useEffect(() => {
    if (!isOpen) {
      setShowDropShadow(false);
    }
  }, [isOpen]);

  /**
   * Handle mouse leave. when mouse leaves the dropdown, set timeout for 250ms
   * If mouse has not re-entered the dropdown, close it. this solves issue
   * with mouse leaving the dropdown when users try to navigate to a category
   * across the page.
   */
  const handleMouseEnter = useCallback(() => {
    if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
    handleOpen();
  }, [handleOpen]);
  const handleMouseLeave = useCallback(() => {
    if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
    hoverTimerRef.current = setTimeout(handleClose, 250);
  }, [handleClose]);
  useEffect(
    () => () => {
      if (hoverTimerRef.current !== null) clearTimeout(hoverTimerRef.current);
    },
    [],
  );

  // Add useEffect to focus dropdown menu after it is rendered to trap focus when dropdown is triggered via hover
  useEffect(() => {
    if (isOpen && dropdownMenuRef.current) {
      dropdownMenuRef.current.focus();
    }
  }, [isOpen]);

  return (
    <Box
      ref={dropdownMenuRef}
      className={className}
      data-has-dropdown-menu={true}
      onMouseEnter={effectiveOpenAction === "hover" ? handleMouseEnter : undefined}
      onMouseLeave={effectiveOpenAction === "hover" ? handleMouseLeave : undefined}
      sx={{
        height: "full",
        position: "relative",
        ...sx,
      }}
      {...props}
    >
      <Button
        type="button"
        ref={dropdownButtonRef}
        aria-label={ariaLabel}
        data-dropdown-button={true}
        onClick={effectiveOpenAction === "click" ? () => setOpen(!isOpen) : undefined}
        onKeyDown={(e) => {
          onKeyDown(e, () => setOpen(!isOpen), ["Enter", " "]);
          onKeyDown(e, () => isOpen && handleClose(), "Escape");
        }}
        variant="dropdown"
        aria-haspopup="menu"
        aria-expanded={isOpen}
        sx={{ px: ["s100", "s200"] }}
      >
        {buttonLabel}
        <ChevronNav
          sx={{
            color: isOpen ? "goldPrimary" : "grey400",
            transition: "transform 0.2s ease-in-out, color 0.2s ease-in-out",
            transform: isOpen ? "rotateX(180deg)" : "none",
          }}
        />
      </Button>

      <Box
        data-dropdown-menu
        className={className}
        tabIndex={-1}
        role="menu"
        aria-orientation="vertical"
        data-show-drop-shadow={showDropShadow}
        onKeyDownCapture={(e) =>
          onKeyDown(
            e,
            () => {
              handleClose();
              dropdownButtonRef.current?.focus();
            },
            "Escape",
          )
        }
        sx={{
          outline: "none",
          overflow: "hidden",
          top: (t) => `calc(100% + ${getThemeValue(t, "space.s200")})`,
          // prevent pointer events when animating to hide (stop nav from reopening after moving mouse out of container)
          pointerEvents: isOpen ? "auto" : "none",
          boxShadow: "none",
          borderRadius: "sm",
          position: "absolute",
          ...(alignment === "right" ? { right: 0, left: "auto" } : ""),
          ...(alignment === "left" ? { left: 0, right: "auto" } : ""),
          ...(alignment === "center" ? { left: "50%", transform: "translateX(-50%)" } : ""),
          zIndex: 10,
          transition: "box-shadow 0.3s ease-in-out",
          '&[data-show-drop-shadow="true"] ': {
            boxShadow: "navProduct",
          },
        }}
      >
        <AnimatePresence>
          {isOpen && (
            <m.div
              initial="hide"
              animate={isOpen ? "show" : "hide"}
              exit="hide"
              variants={{
                hide: {
                  y: "-100%",
                  opacity: 0,
                },
                show: {
                  y: 0,
                  opacity: 1,
                },
              }}
              transition={{ ease: [1, 0, 0, 1], duration: 0.8 }}
              onAnimationComplete={(animation) => setShowDropShadow(isOpen && animation === "show")}
              onClickCapture={(e) => {
                if (shouldCloseOnClick(e.target) && isOpen) {
                  handleClose();
                  const el = e.target as HTMLElement;
                  !el.closest("a[href]") && dropdownButtonRef.current?.focus();
                }
              }}
              sx={{
                position: "relative",
                display: "flex",
                flexDirection: "column",
                width: "full",
                py: "s200",
                bg: "beige50",
                overflow: "visible",
                transformOrigin: "top center",
              }}
            >
              {children}
            </m.div>
          )}
        </AnimatePresence>
      </Box>
    </Box>
  );
};
